import React, { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDate } from '@/utils/dateFormat'
import { useCompanies } from '@/hooks/useCompanies'
import { createCompany, deactivateCompany, updateCompany } from '@/api/companies'
import { PageHeader } from '@/components/ui/PageHeader'
import { DataTable } from '@/components/ui/DataTable'
import { Modal } from '@/components/ui/Modal'
import { Company as CompanyType, ListParams } from '@/types/index'
import { Search, AlertCircle } from 'lucide-react'
import { DEFAULT_PAGE_SIZE } from '@/api/paging'
import { useAuth } from '@/auth/useAuth'
import { UserRole } from '@/types'

const emptyForm = { name: '', domain: '', sourceUrl: '' }

const CompanyList: React.FC = () => {
  const navigate = useNavigate()
  // Bug #370: companies arrive by integration (HubSpot), and create / edit /
  // deactivate are platform-admin only on the backend — so only offer them
  // to platform admins. Everyone else can still browse and view.
  const { user } = useAuth()
  const isPlatformAdmin = !!user?.roles?.includes(UserRole.PlatformAdmin)
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editingCompany, setEditingCompany] = useState<CompanyType | null>(null)
  const [formData, setFormData] = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  // WI #337: a new search is a new result set, so the page resets in the same
  // tick as the term — batched into one render, and therefore one request.
  const [debouncedSearch, setDebouncedSearch] = useState('')
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // WI #337: memoised — `useCompanies` re-fetches on this object's identity,
  // so an inline literal would loop. `GET /companies` searches name + domain
  // server-side, which is what the local filter used to approximate over
  // whichever 20 rows happened to be loaded.
  const listParams = useMemo<ListParams>(
    () => ({
      page,
      pageSize: DEFAULT_PAGE_SIZE,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    }),
    [page, debouncedSearch],
  )

  const { companies, total, isLoading, error, refetch } = useCompanies(listParams)

  const closeModal = () => {
    setCreateModalOpen(false)
    setEditingCompany(null)
    setFormError(null)
  }

  // Bug #331: this used to call the `createCompany` stub in
  // `hooks/useCompanies.ts` — `// API call stub`, `Promise.resolve()` — so
  // the modal closed as though it had saved and nothing was created. It also
  // called create on the *edit* path, which (had it been real) would have
  // produced a duplicate company rather than updating the one being edited.
  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setFormError('Company name is required')
      return
    }
    setSaving(true)
    setFormError(null)
    try {
      const payload = {
        name: formData.name.trim(),
        domain: formData.domain.trim() || undefined,
        sourceUrl: formData.sourceUrl.trim() || undefined,
      }
      if (editingCompany) {
        await updateCompany(editingCompany.id, payload)
      } else {
        // No source_system / source_id: both are optional on create now, and
        // the backend records a hand-created company as 'manual' with a
        // generated identifier (Bug #331). The old modal offered a
        // Salesforce / SAP / NetSuite dropdown — none of which this platform
        // integrates with — and collected no source id at all, so a real
        // POST would have been a 422.
        await createCompany(payload)
      }
      closeModal()
      setFormData(emptyForm)
      await refetch()
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save company')
    } finally {
      setSaving(false)
    }
  }

  // Was `onClick: (_row) => {}` — a literal no-op behind a danger-styled
  // menu item, on top of the API-level defect in `deactivateCompany`.
  const handleDeactivate = async (row: CompanyType) => {
    setActionError(null)
    try {
      await deactivateCompany(row.id)
      await refetch()
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to deactivate company')
    }
  }

  const handleView = (companyId: string) => {
    navigate(`/companies/${companyId}`)
  }

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (value: string) => <div style={{ fontWeight: 500 }}>{value}</div>,
    },
    { key: 'domain', header: 'Domain', render: (value?: string) => value || '-' },
    {
      key: 'sourceSystem',
      header: 'Source system',
      render: (value?: string) =>
        value ? (
          <span
            style={{
              display: 'inline-block',
              padding: '4px 10px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 600,
              backgroundColor: '#dbeafe',
              color: '#0284c7',
            }}
          >
            {value}
          </span>
        ) : (
          '-'
        ),
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (value: boolean) => (
        <span
          style={{
            display: 'inline-block',
            padding: '4px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 600,
            backgroundColor: value ? '#dbeafe' : '#f3f4f6',
            color: value ? '#0284c7' : '#6b7280',
          }}
        >
          {value ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      render: (value?: string) => formatDate(value),
    },
  ]

  return (
    <div style={{ padding: '0' }}>
      <PageHeader
        title="Companies"
        actions={
          isPlatformAdmin && (
            <button
              onClick={() => {
                setEditingCompany(null)
                setFormData(emptyForm)
                setFormError(null)
                setCreateModalOpen(true)
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--color-magenta)',
                color: 'var(--color-white)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              + Add company
            </button>
          )
        }
      />

      <div style={{ padding: '24px' }}>
        {(error || actionError) && (
          <div
            style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              padding: '12px 16px',
              marginBottom: '20px',
              display: 'flex',
              gap: '10px',
              alignItems: 'flex-start',
            }}
          >
            <AlertCircle size={20} style={{ color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ margin: 0, color: '#dc2626', fontWeight: 500 }}>
                {actionError ? 'Action failed' : 'Error loading companies'}
              </p>
              <p style={{ margin: '4px 0 0 0', color: '#991b1b', fontSize: '13px' }}>
                {actionError ?? error?.message ?? 'An error occurred'}
              </p>
            </div>
          </div>
        )}

        {/* Search bar */}
        <div style={{ marginBottom: '20px', position: 'relative' }}>
          <Search
            size={18}
            style={{ position: 'absolute', left: '10px', top: '10px', color: 'var(--color-dark-grey)' }}
          />
          <input
            type="text"
            placeholder="Search companies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px 8px 36px',
              border: '1px solid var(--color-light-grey)',
              borderRadius: '6px',
              fontSize: '14px',
              fontFamily: 'var(--font-family)',
            }}
          />
        </div>

        {/* Data table */}
        {isLoading && companies.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}>
            Loading companies...
          </div>
        ) : companies.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--color-dark-grey)' }}>
            {searchTerm ? 'No companies match your search.' : 'No companies found.'}
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={companies as CompanyType[]}
            rowKey="id"
            isLoading={isLoading}
            totalItems={total}
            currentPage={page}
            pageSize={DEFAULT_PAGE_SIZE}
            onPageChange={setPage}
            // Bug #331: the prop has existed since #166 and every other list
            // page passes it; opening a company meant hamburger → View.
            onRowClick={(row: CompanyType) => handleView(row.id)}
            actions={[
              { label: 'View', onClick: (row: CompanyType) => handleView(row.id) },
              ...(isPlatformAdmin
                ? [
                  {
                    label: 'Edit',
                    onClick: (row: CompanyType) => {
                      setEditingCompany(row)
                      setFormError(null)
                      setFormData({
                        name: row.name,
                        domain: row.domain || '',
                        sourceUrl: row.sourceUrl || '',
                      })
                      setCreateModalOpen(true)
                    },
                  },
                  {
                    label: 'Deactivate',
                    onClick: (row: CompanyType) => void handleDeactivate(row),
                    variant: 'danger' as const,
                  },
                ]
                : []),
            ]}
          />
        )}
      </div>

      {/* Create/Edit modal */}
      <Modal
        isOpen={createModalOpen}
        title={editingCompany ? 'Edit company' : 'Add company'}
        onClose={closeModal}
        isDirty={formData.name !== '' || formData.domain !== '' || formData.sourceUrl !== ''}
        footer={
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={closeModal}
              style={{
                padding: '8px 16px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                backgroundColor: 'var(--color-white)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--color-magenta)',
                color: 'var(--color-white)',
                border: 'none',
                borderRadius: '6px',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
                fontFamily: 'var(--font-family)',
              }}
            >
              {saving ? 'Saving...' : editingCompany ? 'Save' : 'Create'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: '16px' }}>
          {formError && (
            <div style={{ color: '#dc2626', fontSize: '13px' }}>{formError}</div>
          )}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
              Company name (required)
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
              Domain
            </label>
            <input
              type="text"
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
              Source URL
            </label>
            <input
              type="text"
              value={formData.sourceUrl}
              onChange={(e) => setFormData({ ...formData, sourceUrl: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid var(--color-light-grey)',
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'var(--font-family)',
              }}
            />
          </div>
          {!editingCompany && (
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--color-dark-grey)' }}>
              Companies added here are recorded with a source system of
              &ldquo;manual&rdquo;. Companies ingested from HubSpot appear
              automatically.
            </p>
          )}
        </div>
      </Modal>
    </div>
  )
}

export default CompanyList
